import { cn } from '@/lib/cn';
import { Button } from './Button';
import { CategoryMark, colourSolid, colourTint } from './Classification';

/**
 * Suggestions from history, as controls.
 *
 * ── WHY CHIPS AND NOT A SMARTER SELECT ──
 * The person filing 25 statement rows one by one wants the answer where the
 * question is: "Used before for Mahendiran A: ✈️ Travel ×3 · 🍽️ Meals ×1",
 * under the select, one tap. A select that reorders itself by history is
 * the same information hidden behind a tap and a scroll, and a select whose
 * order changes between rows is one the thumb cannot learn.
 *
 * The chips are `ColourChip`'s geometry made pressable: the same tint, the
 * same emoji-or-dot, the count beside the name, and 44px tall under a
 * finger. `aria-pressed` says which one the select currently holds, because
 * the tint is a colour cue and the border alone is another.
 */
export interface SuggestChip {
  id: string;
  name: string;
  emoji?: string | null;
  colour?: string | null;
  count: number;
}

export function SuggestChips({
  label,
  chips,
  value,
  onPick,
  testId,
}: {
  /** "Used before for Mahendiran A" - the sentence the chips complete. */
  label: string;
  chips: SuggestChip[];
  /** The id the field currently holds; that chip reads as pressed. */
  value: string | null;
  onPick: (chip: SuggestChip) => void;
  testId?: string;
}) {
  if (chips.length === 0) return null;
  return (
    <div className="flex flex-col gap-1.5" data-testid={testId}>
      <p className="text-xs text-muted-foreground">{label}:</p>
      <div role="group" aria-label={label} className="flex flex-wrap gap-1.5">
        {chips.map((chip) => {
          const pressed = chip.id === value;
          return (
            <button
              key={chip.id}
              type="button"
              aria-pressed={pressed}
              onClick={() => onPick(chip)}
              className={cn(
                'inline-flex min-h-8 items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium transition-colors touch:min-h-11 touch:px-3.5 touch:text-sm',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                'active:opacity-80',
                pressed ? 'border-current' : 'border-transparent',
              )}
              style={{ backgroundColor: colourTint(chip.colour), color: colourSolid(chip.colour) }}
            >
              <CategoryMark emoji={chip.emoji} colour={chip.colour} />
              <span className="max-w-[12rem] truncate">{chip.name}</span>
              <span className="opacity-80">×{chip.count}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * The line under a field the sheet filled in on its own, with the way back.
 *
 * A polite live region, because the select above it changed without the
 * person touching it, and a screen reader that is not told finds "Travel"
 * where "Other expenses" was and does not know why.
 */
export function SuggestedLine({
  text,
  onUndo,
  testId,
}: {
  text: string;
  onUndo: () => void;
  testId?: string;
}) {
  return (
    <p role="status" className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground" data-testid={testId}>
      <span>{text}</span>
      <Button type="button" variant="link" size="sm" className="h-auto min-h-0 px-0 text-xs touch:min-h-11" onClick={onUndo}>
        Undo
      </Button>
    </p>
  );
}

/**
 * Past descriptions for the same payee, under the description field.
 *
 * A listbox of buttons rather than a native `<datalist>`: the datalist's
 * popup is the browser's own, not styled, not 44px, and on iOS Safari it
 * is a row of tiny grey pills above the keyboard. These are ordinary
 * buttons, so they are a finger's height, they take the theme, and a test
 * can see them. Shown only while there is something to offer that differs
 * from what is typed, so the field is not followed by a copy of itself.
 */
export function PastDescriptions({
  options,
  current,
  onPick,
  testId,
}: {
  options: string[];
  current: string;
  onPick: (description: string) => void;
  testId?: string;
}) {
  const offered = options.filter((option) => option.trim().toLowerCase() !== current.trim().toLowerCase());
  if (offered.length === 0) return null;
  return (
    <ul role="listbox" aria-label="Past descriptions" className="flex flex-col gap-1" data-testid={testId}>
      {offered.map((option) => (
        <li key={option} role="presentation">
          <button
            type="button"
            role="option"
            aria-selected={false}
            onClick={() => onPick(option)}
            className="flex min-h-8 w-full items-center rounded-md border border-border bg-muted/50 px-2.5 text-left text-xs hover:bg-accent active:bg-accent touch:min-h-11 touch:text-sm"
          >
            <span className="truncate">{option}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}
